import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Group, Switch, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import MantineOverflowMenu from 'ui-library/menus/MantineOverflowMenu';

import type {ListItemViewModel} from './listDetailTypes';

const useStyles = createStyles((theme, {selected}: {selected: boolean}) => ({
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${selected ? theme.colors.blue[4] : theme.colors.gray[3]}`,
    backgroundColor: selected ? theme.colors.blue[0] : theme.white,
    width: '100%',
    ':hover': {
      borderColor: theme.colors.blue[3],
      backgroundColor: selected ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  // The detail action. Its `::after` stretches over the whole card so clicking anywhere that isn't
  // the toggle/menu opens the detail view, while the accessible button itself only wraps the title.
  detailButton: {
    textAlign: 'left',
    '::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: theme.radius.sm,
    },
    ':focus-visible::after': {
      outline: `2px solid ${theme.colors.blue[5]}`,
      outlineOffset: theme.spacing.xxs,
    },
  },
  description: {
    marginTop: theme.spacing.xxs,
  },
  actionsContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    flexShrink: 0,
  },
}));

export default function ChatSettingsListItem({
  item,
  selected,
  onClick,
}: {
  item: ListItemViewModel;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const {classes} = useStyles({selected});

  // Disable the toggle while an onChange is in flight so a double-click can't fire a second,
  // conflicting request before the first resolves.
  const [isToggling, setIsToggling] = useState(false);
  const onToggleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.currentTarget.checked;
      setIsToggling(true);
      try {
        await item.toggle?.onChange(checked);
      } finally {
        setIsToggling(false);
      }
    },
    [item.toggle]
  );

  const toggle =
    item.toggle != null ? (
      <Switch
        checked={item.toggle.checked}
        disabled={isToggling || item.toggle.disabled}
        size="xs"
        aria-label={item.toggle.ariaLabel ?? `Toggle ${item.title}`}
        onChange={onToggleChange}
        {...getTestProps({component: 'ChatSettingsListItem', element: 'toggle', key: item.id})}
      />
    ) : null;

  return (
    <div
      className={classes.card}
      {...getTestProps({component: 'ChatSettingsListItem', element: 'row', key: item.id})}
    >
      <div className={classes.cardHeader}>
        {/* The detail action is a real button; its stretched ::after covers the card so a click
            anywhere outside the toggle/menu opens the detail view. */}
        <UnstyledButton
          className={`${classes.cardContent} ${classes.detailButton}`}
          aria-label={`View details for ${item.title}`}
          aria-pressed={selected}
          onClick={onClick}
          {...getTestProps({component: 'ChatSettingsListItem', element: 'detailButton', key: item.id})}
        >
          <Group spacing="xs" noWrap={true} align="center">
            {item.icon}
            <Text fw="bold" size="sm" truncate={true}>
              {item.title}
            </Text>
          </Group>
          {item.subtitle != null && (
            <Text size="xs" c="dimmed">
              {item.subtitle}
            </Text>
          )}
          {item.description != null && (
            <Text size="xs" c="dimmed" lineClamp={2} className={classes.description}>
              {item.description}
            </Text>
          )}
        </UnstyledButton>
        <div className={classes.actionsContainer}>
          {toggle != null &&
            (item.toggle?.tooltip != null ? (
              <Tooltip label={item.toggle.tooltip} withinPortal={true}>
                {toggle}
              </Tooltip>
            ) : (
              toggle
            ))}
          {item.menu != null && item.menu.length > 0 && <MantineOverflowMenu sections={item.menu} />}
        </div>
      </div>
    </div>
  );
}
